import { CheckCircle2, Users, MessageSquare, Zap } from "lucide-react";

export function LandingWhyRaven() {
  const processSteps = [
    {
      icon: Users,
      title: "Escuchamos al Sector",
      description: "Entrevistas con empresarios de seguridad y contraincendios",
    },
    {
      icon: MessageSquare,
      title: "Identificamos Necesidades",
      description: "Cada funcionalidad nace de un problema real del día a día",
    },
    {
      icon: Zap,
      title: "Desarrollamos Juntos",
      description: "Validación continua con profesionales en activo",
    },
    {
      icon: CheckCircle2,
      title: "Probado en el Campo",
      description: "Testado en operaciones reales antes de cada lanzamiento",
    },
  ];

  const keyPoints = [
    "No es solo software genérico adaptado - está diseñado desde cero para tu sector",
    "Entiende la complejidad del trabajo offline en instalaciones",
    "Habla tu idioma: órdenes, partes, revisiones, anomalías, checklists",
    "Evoluciona constantemente con feedback de empresarios como tú",
    "Operativo al instante - sin servidores, sin complicaciones técnicas",
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-b from-background via-muted/10 to-background">
      {/* Background decoration */}
      <div className="absolute top-1/2 left-0 w-96 h-96 rounded-full bg-raven-violet/10 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-raven-electric/10 blur-3xl" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            ¿Por qué <span className="landing-gradient-text">RAVEN</span>?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            RAVEN es la plataforma de gestión diseñada específicamente para empresas de <span className="font-semibold text-foreground">seguridad</span> y <span className="font-semibold text-foreground">contraincendios</span>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          
          {/* Left Column - What is RAVEN */}
          <div className="space-y-6 animate-fade-in" style={{animationDelay: '0.2s'}}>
            <div className="landing-glass-card p-8">
              <h3 className="text-2xl font-bold mb-6 text-foreground">
                Desarrollado con <span className="landing-gradient-text">Profesionales del Sector</span>
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                No es solo una app más. RAVEN fue construido escuchando activamente a empresarios de seguridad y contraincendios, entendiendo sus desafíos diarios y diseñando soluciones reales.
              </p>
              
              <div className="space-y-4">
                {keyPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-3 group">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-raven-electric to-raven-violet flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-foreground leading-relaxed">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Process Timeline */}
          <div className="space-y-6 animate-fade-in" style={{animationDelay: '0.4s'}}>
            <div className="landing-glass-card p-8">
              <h3 className="text-2xl font-bold mb-8 text-foreground">
                Nuestro Proceso de <span className="landing-gradient-text">Co-creación</span>
              </h3>
              
              <div className="space-y-6">
                {processSteps.map((step, index) => (
                  <div 
                    key={index}
                    className="relative flex gap-4 group"
                  >
                    {/* Timeline line */}
                    {index < processSteps.length - 1 && (
                      <div className="absolute left-7 top-16 w-0.5 h-full bg-gradient-to-b from-raven-electric to-raven-violet opacity-30" />
                    )}
                    
                    {/* Icon */}
                    <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-raven-electric to-raven-violet flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-raven-electric/30">
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pt-2">
                      <h4 className="font-bold text-lg mb-1 text-foreground group-hover:text-raven-electric transition-colors">
                        {step.title}
                      </h4>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Bottom highlight */}
        <div className="text-center landing-glass-card p-8 animate-fade-in" style={{animationDelay: '0.6s'}}>
          <p className="text-xl text-foreground font-semibold mb-2">
            Cada funcionalidad surgió de una <span className="landing-gradient-text">necesidad real</span>
          </p>
          <p className="text-muted-foreground">
            Probada y validada por profesionales en activo antes de llegar a tus manos
          </p>
        </div>
      </div>
    </section>
  );
}
